/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { externalCredentialsCreateGcpIam } from "../funcs/externalCredentialsCreateGcpIam.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GcpIamCredential } from "../models/components/gcpiamcredential.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateGcpIamCredentialRequest,
  CreateGcpIamCredentialSecurity,
} from "../models/operations/creategcpiamcredential.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateGcpIamCredentialMutationVariables = {
  request: CreateGcpIamCredentialRequest;
  security?: CreateGcpIamCredentialSecurity | undefined;
  options?: RequestOptions;
};

export type CreateGcpIamCredentialMutationData = GcpIamCredential;

export type CreateGcpIamCredentialMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createGcpIamCredential externalCredentials
 *
 * @remarks
 * Create a GCP IAM external credential. Requires org:admin.
 */
export function useCreateGcpIamCredentialMutation(
  options?: MutationHookOptions<
    CreateGcpIamCredentialMutationData,
    CreateGcpIamCredentialMutationError,
    CreateGcpIamCredentialMutationVariables
  >,
): UseMutationResult<
  CreateGcpIamCredentialMutationData,
  CreateGcpIamCredentialMutationError,
  CreateGcpIamCredentialMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateGcpIamCredentialMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateGcpIamCredential(): MutationKey {
  return ["@gram/client", "externalCredentials", "createGcpIam"];
}

export function buildCreateGcpIamCredentialMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateGcpIamCredentialMutationVariables,
  ) => Promise<CreateGcpIamCredentialMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateGcpIamCredential(),
    mutationFn: function createGcpIamCredentialMutationFn({
      request,
      security,
      options,
    }): Promise<CreateGcpIamCredentialMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(externalCredentialsCreateGcpIam(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
